import { useMemo, useRef } from 'react';
/**
 * Mobile Phase 1 fix: after a long-press has fired, lifting the finger still makes the browser send a click to whatever
 * is under it. That is often a row of the action sheet that has just opened (Reply in thread, Make task...), so the
 * release would activate it. swallowNextClick() eats the clicks of the release (capture phase on window, before React
 * sees them) for a short window; a real tap after the window works normally.
 */
export const GHOST_CLICK_MS = 450;

export function swallowNextClick(ms = GHOST_CLICK_MS): () => void {
  const until = Date.now() + ms;
  const onClick = (e: Event) => {
    if (Date.now() > until) { stop(); return; }
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  };
  const timer = setTimeout(() => stop(), ms);
  function stop() { clearTimeout(timer); window.removeEventListener('click', onClick, true); }
  window.addEventListener('click', onClick, true);
  return stop;
}

/** How long a finger must rest on a message or comment before its action sheet opens (chat and comments alike). */
export const LONG_PRESS_MS = 550;
const MOVE_TOLERANCE_PX = 8;

/**
 * t189u: the chat long-press for any comment row (task comments): hold still for LONG_PRESS_MS (or Android's
 * contextmenu) opens `onLong`; moving cancels; the release after it fired never taps what is under the finger.
 * Spread the returned handlers on the row.
 */
export function longPressHandlers(onLong: () => void, enabled = true) {
  let s: { x: number; y: number; timer: ReturnType<typeof setTimeout>; fired: boolean } | null = null;
  const fire = () => { if (!s) return; s.fired = true; window.getSelection()?.removeAllRanges(); navigator.vibrate?.(10); onLong(); };
  return {
    onTouchStart: (e: { touches: ArrayLike<{ clientX: number; clientY: number }> }) => {
      if (!enabled) return;
      const t = e.touches[0]!;
      if (s) clearTimeout(s.timer);
      s = { x: t.clientX, y: t.clientY, fired: false, timer: setTimeout(fire, LONG_PRESS_MS) };
    },
    onTouchMove: (e: { touches: ArrayLike<{ clientX: number; clientY: number }> }) => {
      if (!s || s.fired) return;
      const t = e.touches[0]!;
      if (Math.abs(t.clientX - s.x) > MOVE_TOLERANCE_PX || Math.abs(t.clientY - s.y) > MOVE_TOLERANCE_PX) { clearTimeout(s.timer); s = null; }
    },
    onTouchEnd: (e: { cancelable: boolean; preventDefault: () => void }) => {
      const s0 = s; s = null;
      if (!s0) return;
      clearTimeout(s0.timer);
      if (s0.fired) { if (e.cancelable) e.preventDefault(); swallowNextClick(); }
    },
    onTouchCancel: () => { if (s) clearTimeout(s.timer); s = null; },
    onContextMenu: (e: { preventDefault: () => void }) => {
      if (!enabled) return;
      e.preventDefault();
      if (s?.fired) return;
      if (s) clearTimeout(s.timer);
      s = { x: 0, y: 0, fired: false, timer: setTimeout(() => undefined, 0) };
      fire();
    },
  };
}

/** longPressHandlers kept across renders (the latest onLong is used). */
export function useLongPress(onLong: () => void, enabled = true) {
  const cb = useRef(onLong);
  cb.current = onLong;
  return useMemo(() => longPressHandlers(() => cb.current(), enabled), [enabled]);
}
